import { isEmpty } from '#misc/is-empty';
import { isNotDefined } from '#misc/is-not-defined.guard';

/**
 * Type guard that checks if the provided value is a number.
 *
 * A value is considered a number if it is:
 * - A primitive number (e.g., `42`, `3.14`)
 * - An instance of the `Number` object
 * - A string that can be converted to a number (e.g., `"42"`, `"3.14"`)
 *
 * @param value - The value to check.
 * @returns `true` if the value is a number, otherwise `false`.
 *
 *  @example
 * ```typescript
 * isNumber(42); // true
 * isNumber(new Number(42)); // true
 * isNumber('42'); // true
 * isNumber('3.14'); // true
 * isNumber('invalid'); // false
 * isNumber(null); // false
 * isNumber(undefined); // false
 * isNumber({}); // false
 * isNumber([]); // false
 * isNumber(NaN); // false
 * isNumber(Infinity); // true
 * isNumber(-Infinity); // true
 * ```
 */
export const isNumber = (value: unknown): value is number => {
  if (isEmpty(value)) {
    return false;
  }

  if (isNotDefined(value)) {
    return false;
  }

  // check if the value is a number type (catches primitive numbers)
  if (typeof value === 'number') {
    return true;
  }

  // check if the value is a Number object
  // (we want to allow Number objects here)
  // eslint-disable-next-line unicorn/no-instanceof-builtins
  if (value instanceof Number) {
    return true;
  }

  if (typeof value === 'string') {
    return !Number.isNaN(Number(value.trim()));
  }

  return false;
};
